/* assets/sham-savings-calculator.js */

class ShamSavingsCalculator extends HTMLElement {
  // parseFloat/parseInt fall back on any falsy result, which would treat an
  // explicit 0 (e.g. a discontinued Loyalty Box) the same as a missing or
  // garbled attribute. Fall back only on NaN so a real 0 renders as $0.
  static numberOr(raw, fallback) {
    const n = parseFloat(raw);
    return Number.isNaN(n) ? fallback : n;
  }

  static integerOr(raw, fallback) {
    const n = parseInt(raw, 10);
    return Number.isNaN(n) ? fallback : n;
  }

  constructor() {
    super();
    this.state = { weeks: 4, equivType: 'tomahawk' };

    this.pills = this.querySelectorAll('.sham-pill');
    this.pillBlurb = this.querySelector('#pill-blurb');
    this.dispTotal = this.querySelector('#disp-total');
    this.dispProtein = this.querySelector('#disp-protein');
    this.dispLoyalty = this.querySelector('#disp-loyalty');
    this.equivSelect = this.querySelector('#equiv-select');
    this.emojiBlock = this.querySelector('#emoji-block');
    this.equivQty = this.querySelector('#equiv-qty');
  }

  connectedCallback() {
    this.initConfig();
    this.bindEvents();
    this.render();
    requestAnimationFrame(() => this.updatePillBlurbPosition());
    window.addEventListener('resize', () => this.updatePillBlurbPosition());
  }

  initConfig() {
    this.config = {
      packValue: ShamSavingsCalculator.numberOr(this.dataset.packValue, 17),
      loyaltyBoxValue: ShamSavingsCalculator.numberOr(this.dataset.loyaltyValue, 79),
      blurbWeeks: ShamSavingsCalculator.integerOr(this.dataset.blurbWeeks, 3),
      equiv1Name: this.dataset.equiv1Name || 'tomahawk steaks',
      equiv1Price: ShamSavingsCalculator.numberOr(this.dataset.equiv1Price, 80),
      equiv1Emoji: this.dataset.equiv1Emoji || '🥩',
      equiv2Name: this.dataset.equiv2Name || 'salmon whole sides',
      equiv2Price: ShamSavingsCalculator.numberOr(this.dataset.equiv2Price, 90),
      equiv2Emoji: this.dataset.equiv2Emoji || '🐟',
    };
    this.state.weeks = ShamSavingsCalculator.integerOr(this.dataset.defaultWeeks, 4);
  }

  bindEvents() {
    this.pills.forEach(pill => {
      pill.addEventListener('click', (e) => {
        this.pills.forEach(p => p.classList.remove('is-active'));
        e.currentTarget.classList.add('is-active');
        this.state.weeks = parseInt(e.currentTarget.dataset.val, 10);
        this.updatePillBlurbPosition();
        this.render();
      });
    });

    if (this.equivSelect) {
      this.equivSelect.addEventListener('change', (e) => {
        this.state.equivType = e.target.value;
        this.render();
      });
    }
  }

  updatePillBlurbPosition() {
    if (!this.pillBlurb) return;
    // Deliberately NOT the active pill: the pin marks the popular cadence,
    // the active pill marks the conservative default. See the section schema.
    const blurbPill = Array.from(this.pills).find(
      (p) => parseInt(p.dataset.val, 10) === this.config.blurbWeeks
    );
    if (!blurbPill) return;
    const parentRect = blurbPill.parentElement.getBoundingClientRect();
    const pillRect = blurbPill.getBoundingClientRect();
    this.pillBlurb.style.left = `${(pillRect.left - parentRect.left) + (pillRect.width / 2)}px`;
  }

  render() {
    const value = ShamSubscriptionValue.firstYearValue({
      weeks: this.state.weeks,
      packValue: this.config.packValue,
      loyaltyBoxValue: this.config.loyaltyBoxValue,
    });

    if (this.dispTotal) this.dispTotal.textContent = `$${value.total}`;
    if (this.dispProtein) this.dispProtein.textContent = `$${value.protein}`;
    if (this.dispLoyalty) this.dispLoyalty.textContent = `$${value.loyaltyBox}`;

    this.renderEquivalent(value.total);
  }

  renderEquivalent(total) {
    const isFirst = this.state.equivType === 'tomahawk';
    const price = isFirst ? this.config.equiv1Price : this.config.equiv2Price;
    const emoji = isFirst ? this.config.equiv1Emoji : this.config.equiv2Emoji;

    const qty = ShamSubscriptionValue.equivalentQuantity(total, price);
    if (this.equivQty) this.equivQty.textContent = `${qty.toFixed(1)}`;

    this.renderEmojis(qty, emoji);
  }

  renderEmojis(qtyRaw, emojiStr) {
    if (!this.emojiBlock) return;
    this.emojiBlock.innerHTML = '';

    const num = parseFloat((qtyRaw).toFixed(1));
    const fullCount = Math.floor(num);
    const fraction = num - fullCount;

    for (let i = 0; i < fullCount; i++) {
        const span = document.createElement('span');
        span.className = 'sham-emoji-char';
        span.textContent = emojiStr;
        this.emojiBlock.appendChild(span);
    }

    if (fraction >= 0.1) {
        const wrapSpan = document.createElement('span');
        wrapSpan.className = 'sham-emoji-char is-partial';
        const clipPct = 100 - (fraction * 100);
        wrapSpan.style.clipPath = `inset(0 ${clipPct}% 0 0)`;
        wrapSpan.textContent = emojiStr;
        this.emojiBlock.appendChild(wrapSpan);
    }
  }
}

if (!customElements.get('sham-savings-calculator')) {
  customElements.define('sham-savings-calculator', ShamSavingsCalculator);
}
